import {
  Facet,
  KeyFacet,
  ProgressKeyFacet,
  progressBatchKeyFacet,
  stringPart,
  subKeyPart
} from "@ms/items-view";
import {
  ICloudStorageFolder,
  provisionSpecialDocumentLibraries,
  ISiteInfo,
  getChannelCloudStorageFolders
} from "@msteams/bridges-models-files";

const cloudStorageItem = "cloudStorageItem";
const cloudStorageDataSourceConfiguration =
  "cloudStorageDataSourceConfigurationFacet";
const cloudStorageProvider = "cloudStorageProvider";

const cloudStorageKeyFacet: KeyFacet<{}> = new KeyFacet(
  "cloudStorageKeyFacet",
  {}
);

const threadIdKeyFacet: KeyFacet<{}> = new KeyFacet("threadIdKeyFacet", {});
const teamThreadIdKeyFacet: KeyFacet<{}> = new KeyFacet("threadIdKeyFacet", {});

export const googleDriveItemKeyFacet: KeyFacet<{
  itemId: string;
  name: string;
  parentFolder: string;
}> = new KeyFacet("googledriveItem", {
  itemId: stringPart(),
  name: stringPart(),
  parentFolder: stringPart()
});
export const googleDriveItemChildrenKeyFacet: KeyFacet<{
  ParentItemKey: typeof googleDriveItemKeyFacet.type;
}> = new KeyFacet("googledriveItemChildren", {
  ParentItemKey: subKeyPart(googleDriveItemKeyFacet)
});
export const cloudStorageItemKeyFacet: KeyFacet<{
  webAbsoluteUrl: string;
  folderPath: string;
}> = new KeyFacet(cloudStorageItem, {
  webAbsoluteUrl: stringPart(),
  folderPath: stringPart()
});
/**
 * provideName === providerCode, ExternalFilesProviderCode values like "GOOGLEDRIVE", "DROPBOX", "BOX"
 * providerId === providerType, FilesProviderType values like Enum -  0 (Sharepoint), 1 (WOPI), 2 (Google)
 * Values have be serialized as string but represent actual interface values
 */
export const cloudStorageProviderKeyFacet: KeyFacet<{
  id: string;
  folderId: string;
  title: string;
  ownerDisplayName: string;
  providerName: string;
  providerId: string;
  siteUrl: string;
  serverRelativeUrl: string;
  libraryType?: string;
}> = new KeyFacet(cloudStorageProvider, {
  id: stringPart(),
  folderId: stringPart(),
  title: stringPart(),
  ownerDisplayName: stringPart(),
  providerName: stringPart(),
  providerId: stringPart(),
  siteUrl: stringPart(),
  serverRelativeUrl: stringPart(),
  libraryType: stringPart()
});
const oneDriveItemScopeItemKeyFacet = new KeyFacet(
  "oneDriveItemScopeItemKeyFacet",
  {}
);
const oneDriveOwnerKeyFacet = new KeyFacet("oneDriveOwnerKeyFacet", {});
const loggingPrefixKeyFacet: KeyFacet<{}> = new KeyFacet(
  "loggingPrefixKeyFacet",
  {}
);
const provisionSpecialDocumentLibraryKeyFacet: KeyFacet<{}> = new KeyFacet(
  "provisionSpecialDocumentLibraryKeyFacet",
  {}
);
const siteInfoKeyFacet: KeyFacet<{}> = new KeyFacet("siteInfoKeyFacet", {});
/**
 * This facet is to track if there is any instance of upload progress monitor running.
 * We use this to avoid triggering another upload monitor
 */
const uploadProgressTrackerKeyFacet = new KeyFacet<{}>(
  "uploadProgressTrackerKeyFacet",
  {}
);
export const isUploadMonitorActiveFacet = new Facet<boolean>(
  "isUploadMonitorActiveFacet"
);

/**
 * item key's for accessing items from store's cache
 */
export const itemCacheStoreFilesKeys = {
  oneDriveItemScopeItemKey: oneDriveItemScopeItemKeyFacet.serialize({}),
  oneDriveOwner: oneDriveOwnerKeyFacet.serialize({}),
  loggingPrefixKey: loggingPrefixKeyFacet.serialize({}),
  threadIdKey: threadIdKeyFacet.serialize({}),
  teamThreadIdKey: teamThreadIdKeyFacet.serialize({}),
  provisionSpecialDocumentLibraryKey: provisionSpecialDocumentLibraryKeyFacet.serialize(
    {}
  ),
  siteInfoKey: siteInfoKeyFacet.serialize({}),
  cloudStorageKey: cloudStorageKeyFacet.serialize({}),
  uploadProgressTrackerKey: uploadProgressTrackerKeyFacet.serialize({})
};

export const cloudStorageDataSourceConfigurationFacet: Facet<{
  cloudStorageFolderListFunc: () => Promise<ICloudStorageFolder[]>;
}> = new Facet(cloudStorageDataSourceConfiguration);
export const oneDriveItemScopeItemFacet = new Facet<string>(
  "oneDriveItemScopeItem"
);
/**
 * Each item stored in the item cache is added with an owner. The same item
 * is accessed and deleted from the store using the same owner. Hence, maintain
 * a single copy of the owner.
 */
export const oneDriveOwnerFacet = new Facet<{}>("oneDriveOwner");
export const loggingPrefixFacet = new Facet<string>("loggingPrefix");

/**
 * Wopi facets
 */
export const wopiItemKeyFacet: KeyFacet<{
  itemId: string;
  name: string;
  providerCode: string;
  parentFolder: string;
  accessToken?: string;
}> = new KeyFacet("wopiItemKey", {
  itemId: stringPart(),
  name: stringPart(),
  providerCode: stringPart(),
  parentFolder: stringPart(),
  accessToken: stringPart()
});

export const wopiChildrenItemKeyFacet: KeyFacet<{
  ParentItemKey: typeof wopiItemKeyFacet.type;
}> = new KeyFacet("wopiChildrenItemKeyFacet", {
  ParentItemKey: subKeyPart(wopiItemKeyFacet)
});
export const provisionSpecialDocumentLibraryFacet = new Facet<
  provisionSpecialDocumentLibraries
>("provisionSpecialDocumentLibraries");
export const threadIdFacet = new Facet<string>("threadId");
export const teamThreadIdFacet = new Facet<string>("teamThreadId");
export const siteInfoFacet = new Facet<ISiteInfo>("siteInfo");
export const cloudStorageFacet = new Facet<getChannelCloudStorageFolders>(
  "cloudStorage"
);

/**
 * item-load-progress facet & keys
 * progressBatchKeyFacet.serializeNext() - returns the auto incremented next available progress batch key
 */
export const itemLoadProgressKeyFacet = new ProgressKeyFacet(
  "itemLoadProgress"
);
const nextProgressBatchKey = progressBatchKeyFacet.serializeNext();
export const itemLoadProgressBatchKey = itemLoadProgressKeyFacet.serializeBatchKey(
  nextProgressBatchKey
);
